import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEbayConnection } from '@/hooks/useEbayConnection';

const STORAGE_KEY = 'ebay:lastAutoSyncAt';
const MIN_INTERVAL_MS = 90 * 1000; // throttle across tabs/reloads/focus
const POLL_INTERVAL_MS = 3 * 60 * 1000; // while the tab is visible (server cron covers every 5 min)

/**
 * Keeps eBay sales fresh in near-real-time for signed-in users with an active
 * eBay connection: syncs on mount, every 60s while the tab is visible, and
 * immediately when the tab regains focus/visibility (throttled to 45s).
 *
 * Silent: no toasts. The manual Sync button on MarketplaceConnectionsCard
 * still surfaces feedback when invoked directly.
 */
export function useEbayAutoSync() {
	const { data: connection } = useEbayConnection();
	const queryClient = useQueryClient();
	const inFlight = useRef(false);

	useEffect(() => {
		if (!connection?.is_active) return;

		let cancelled = false;

		const runSync = async (force = false) => {
			if (inFlight.current) return;
			if (!force) {
				try {
					const last = Number(window.localStorage.getItem(STORAGE_KEY) ?? 0);
					if (Date.now() - last < MIN_INTERVAL_MS) return;
				} catch {
					// localStorage unavailable — still attempt
				}
			}
			try {
				window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
			} catch {
				/* noop */
			}

			inFlight.current = true;
			try {
				const { data, error } = await supabase.functions.invoke('ebay-sync-orders', {
					body: {}
				});
				if (error) {
					console.warn('[ebay-auto-sync] sync error:', error);
					return;
				}
				if (cancelled) return;
				const created = (data as any)?.results?.[0]?.transactions_created ?? 0;
				if (created > 0) {
					queryClient.invalidateQueries({ queryKey: ['transactions'] });
					queryClient.invalidateQueries({ queryKey: ['user-items'] });
					queryClient.invalidateQueries({ queryKey: ['ebay-listings'] });
					queryClient.invalidateQueries({ queryKey: ['ebay-orders'] });
					queryClient.invalidateQueries({ queryKey: ['notifications'] });
					queryClient.invalidateQueries({ queryKey: ['unread-count'] });
					queryClient.invalidateQueries({ queryKey: ['inbox-unread-counters'] });
				}
			} catch (err) {
				console.warn('[ebay-auto-sync] sync threw:', err);
			} finally {
				inFlight.current = false;
			}
		};

		runSync();

		const interval = window.setInterval(() => {
			if (document.visibilityState === 'visible') void runSync();
		}, POLL_INTERVAL_MS);

		const onVisible = () => {
			if (document.visibilityState === 'visible') void runSync();
		};
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onVisible);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onVisible);
		};
	}, [connection?.is_active, queryClient]);
}
